import Footer from "units/dashboard/components/Footer";
import ClientSidebar from "./ClientSidebar";
import { useNavigate, useParams } from "react-router-dom";
import ClientHeader from "./ClientHeader";
import { useDispatch, useSelector } from "react-redux";
import { getUserType } from "../../dashboard/services/getUserProfile";
import { setUser } from "../../dashboard/store/userSlice";
import { useEffect, useState } from "react";
import { useClientConversations } from "../hooks/useClientConversations";
import { getBotInfo } from "units/bot/services/botService";
import { chatbotMessagesList } from "../services/clientChatBotService";
import {
    MessageSquare,
    Users,
    Bot,
    TrendingUp,
    Calendar,
    Clock,
    ChevronRight,
    Wallet,
    Plus,
    ArrowUpRight,
    ArrowDownLeft
} from "lucide-react";
import { getWalletAmount } from "../services/ClientProfileService";

const ClientDashboard = () => {
    const [conversations, setConversations] = useState([]);
    const [error, setError] = useState(null);
    const [ownerId, setOwnerId] = useState("");
    const [botId, setBotId] = useState(0);
    const [userId, setUserId] = useState("");
    const [name, setName] = useState("");
    const { username } = useParams();
    const [chats, setChats] = useState(0);
    const [loading, setLoading] = useState(true);
    const [walletBalance, setWalletBalance] = useState(0);
    const [botStatus, setBotStatus] = useState("no");
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const accessToken = useSelector((state) => state.auth.accessToken);
    const user = useSelector((state) => state.userInfo);

    useEffect(() => {
        const handleWalletUpdate = () => {
            const getWallet = async () => {
                try {
                    const res = await getWalletAmount({
                        username,
                        accessToken,
                    });
                    if (res?.data.ok) {
                        setWalletBalance(res.data?.data.balance ?? 0);
                    }
                } catch (err) {
                    console.log("wallet update error:", err);
                }
            };

            if (username && accessToken) {
                getWallet();
            }
        };

        window.addEventListener("walletUpdated", handleWalletUpdate);
        handleWalletUpdate()
        return () => {
            window.removeEventListener(
                "walletUpdated",
                handleWalletUpdate
            );
        };
    }, [username, accessToken]);

    useEffect(() => {
        if (!accessToken) return;

        const fetchUser = async () => {
            try {
                const res = await getUserType(accessToken);
                if (res?.ok && res?.data) {
                    setUserId(res?.data?.id);
                }
            } catch (error) {
                console.error("خطا در دریافت اطلاعات کاربر:", error);
            }
        };

        fetchUser();
    }, [accessToken]);

    useEffect(() => {
        const fetchData = async () => {
            if (!accessToken) return;
            setLoading(true);
            try {
                const botInfo = await getBotInfo(username);
                if (botInfo?.ok) {
                    setName(botInfo?.data?.name);
                    setBotId(botInfo?.data?.id);
                    setOwnerId(botInfo?.data?.ownerId);
                    setBotStatus(botInfo.data.status);
                }

                if (botInfo?.data?.ownerId === userId) {
                    const data = await chatbotMessagesList({ accessToken, username });
                    if (data?.ok) {
                        if (Array.isArray(data?.data)) {
                            setChats(data.data.filter(e => e.withGPT && e.receiverId === botInfo.data.id).length);
                        }
                    } else {
                        setError("مشکلی در دریافت مکالمات پیش آمد");
                    }
                } else {
                    if (botInfo?.data?.status === "accepted") {
                        const data = await chatbotMessagesList({ accessToken, username });
                        if (data?.ok) {
                            if (Array.isArray(data?.data)) {
                                setChats(data.data.filter(e => e.withGPT && e.receiverId === botInfo.data.id).length);
                            }
                        } else {
                            setError("مشکلی در دریافت مکالمات پیش آمد");
                        }
                    }
                }
            } catch (error) {
                console.error(error);
                setError("خطایی رخ داد. لطفاً دوباره تلاش کنید.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [accessToken, username, userId]);

    // کارت‌های آمار
    const statsCards = [
        {
            id: 1,
            title: "تعداد گفتگوها",
            value: chats,
            icon: <MessageSquare className="w-6 h-6" />,
            color: "from-[#1B4748] to-[#2d6b6c]",
            bg: "bg-[#aadec5]/20",
            textColor: "text-[#1B4748]"
        },
        {
            id: 2,
            title: "نام بات",
            value: name?.trim() || username || "نامشخص",
            icon: <Bot className="w-6 h-6" />,
            color: "from-[#ff9f20] to-[#ff7520]",
            bg: "bg-[#ff9f20]/20",
            textColor: "text-[#ff9f20]"
        },
        {
            id: 3,
            title: "وضعیت",
            value: "فعال",
            icon: <TrendingUp className="w-6 h-6" />,
            color: "from-green-500 to-green-600",
            bg: "bg-green-100",
            textColor: "text-green-600"
        }
    ];

    // فرمت کردن عدد با کاما
    const formatNumber = (num) => {
        return num?.toString()?.replace(/\B(?=(\d{3})+(?!\d))/g, ",") || "۰";
    };

    return (
        <>
            {/* هدر */}
            <nav className="border-b border-slate-100 dark:bg-zinc-800 print:hidden flex items-center fixed top-0 right-0 left-0 bg-white z-10 dark:border-zinc-700">
                <ClientHeader username={username} />
            </nav>

            {/* سایدبار */}
            <div className="vertical-menu rtl:right-0 fixed ltr:left-0 bottom-0 top-[70px] h-screen border-l bg-slate-50 border-gray-50 print:hidden dark:bg-zinc-800 dark:border-neutral-700 z-10 w-[260px]">
                <ClientSidebar username={username} />
            </div>

            {/* محتوای اصلی */}
            <div className="flex flex-col min-h-screen">
                <div className="main-content flex-grow pr-0">
                    <div className="page-content dark:bg-zinc-800/95 min-h-screen bg-gradient-to-br from-gray-50/80 via-white to-gray-50/40 dark:from-zinc-800 dark:via-zinc-800/95 dark:to-zinc-900/90">
                        <div className="px-4 md:px-6 py-4 md:py-6">

                            {/* هدر داشبورد */}
                            <div className="flex items-start sm:items-center justify-between gap-4 mb-6">
                                <div>
                                    <h4 className="text-xl font-bold text-gray-800 dark:text-gray-100 flex items-center gap-2">
                                        <span className="p-2 rounded-xl bg-[#aadec5]/30 text-[#1B4748]">
                                            <Bot className="w-5 h-5" />
                                        </span>
                                        داشبورد
                                    </h4>
                                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mr-11">
                                        کاربر محترم به داشبورد کاربری خوش آمدید
                                    </p>
                                </div>
                                <div className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500">
                                    <Calendar className="w-4 h-4" />
                                    <span>{new Date().toLocaleDateString('fa-IR')}</span>
                                </div>
                            </div>

                            {/* خط تزئینی */}
                            <div className="relative mb-8">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-200/60 dark:border-zinc-700/50"></div>
                                </div>
                                <div className="relative flex justify-start">
                                    <div className="w-16 h-0.5 bg-gradient-to-r from-[#1B4748] to-[#aadec5] rounded-full"></div>
                                </div>
                            </div>

                            {/* لودینگ */}
                            {loading ? (
                                <div className="flex justify-center items-center h-[40vh]">
                                    <div className="flex flex-col items-center gap-4">
                                        <div className="w-12 h-12 border-4 border-gray-200 border-t-[#1B4748] rounded-full animate-spin"></div>
                                        <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">
                                            در حال بارگذاری...
                                        </p>
                                    </div>
                                </div>
                            ) : error ? (
                                <div className="flex justify-center items-center h-[40vh]">
                                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-6 text-center max-w-md">
                                        <p className="text-red-600 dark:text-red-400">{error}</p>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {/* بخش اطلاعات اضافی */}
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                        {/* کارت وضعیت بات */}
                                        <div className="bg-gray-100/50 dark:bg-zinc-800/90 rounded-2xl shadow-lg border border-gray-100/50 dark:border-zinc-700/50 p-6">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="p-2 rounded-xl bg-[#ff9900]/30 dark:bg-green-900/20">
                                                    <Bot className="w-5 h-5 text-[#bb7000] dark:text-green-400" />
                                                </div>
                                                <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                                    وضعیت بات
                                                </h5>
                                            </div>
                                            {botStatus == "accepted" ? (
                                                <div className="flex items-center gap-3">
                                                    <div className="w-2.5 h-2.5 rounded-full bg-green-500 animate-pulse"></div>
                                                    <span className="text-sm text-gray-600 dark:text-gray-300">فعال و آماده پاسخگویی</span>
                                                </div>
                                            ) : (
                                                <div className="flex items-center gap-3">
                                                    <div className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></div>
                                                    <span className="text-sm text-gray-600 dark:text-gray-300">غیرفعال</span>
                                                </div>
                                            )}
                                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-3">
                                                نام بات: {name?.trim() || username || "نامشخص"}
                                            </p>
                                        </div>

                                        {/* کارت کیف پول */}
                                        <div className="bg-gradient-to-br from-[#1B4748] to-[#2d6b6c] rounded-2xl shadow-xl border border-[#1B4748]/20 p-6 text-white relative overflow-hidden">
                                            <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                                            <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
                                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-white/[0.02] rounded-full blur-2xl"></div>

                                            <div className="relative">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center gap-2">
                                                        <div className="p-1.5 bg-[#ff9900]/50 rounded-lg">
                                                            <Wallet className="w-4 h-4 text-[#ffcb7c]" />
                                                        </div>
                                                        <h5 className="text-sm font-semibold text-white/90">
                                                            کیف پول
                                                        </h5>
                                                    </div>
                                                    <button
                                                        onClick={() => navigate(`/bot/wallet/${username}`)}
                                                        className="text-xs text-white/70 hover:text-white transition-colors flex items-center gap-0.5 bg-white/10 px-2 py-1 rounded-lg"
                                                    >
                                                        <Plus className="w-3 h-3" />
                                                        شارژ
                                                    </button>
                                                </div>

                                                <div className="mb-2">
                                                    <p className="text-3xl font-bold">
                                                        {formatNumber(walletBalance)}
                                                        <span className="text-sm font-normal text-white/70 mr-1">
                                                            تومان
                                                        </span>
                                                    </p>
                                                    <p className="text-xs text-white/50 mt-0.5">
                                                        موجودی کیف پول شما
                                                    </p>
                                                </div>

                                                {/* <div className="flex items-center gap-4 pt-3 border-t border-white/10">
                                                    <div className="flex items-center gap-1.5">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400"></div>
                                                        <span className="text-xs text-white/60">واریز</span>
                                                        <span className="text-xs text-white/90">+۱۲,۰۰۰</span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5">
                                                        <div className="w-1.5 h-1.5 rounded-full bg-rose-400"></div>
                                                        <span className="text-xs text-white/60">برداشت</span>
                                                        <span className="text-xs text-white/90">-۳,۵۰۰</span>
                                                    </div>
                                                </div> */}
                                            </div>
                                        </div> 
                                        <div className="grid grid-cols-1 gap-6">
                                            <div className="bg-gray-100/50 dark:bg-zinc-800/90 rounded-2xl shadow-lg border border-gray-100/50 dark:border-zinc-700/50 p-6">
                                                <div className="flex items-center gap-3 mb-4">
                                                    <div className="p-2 rounded-xl bg-[#ff9900]/30">
                                                        <MessageSquare className="w-5 h-5 text-[#bb7000]" />
                                                    </div>
                                                    <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                                        آمار گفتگوها
                                                    </h5>
                                                </div>
                                                <div className="flex items-end justify-between">
                                                    <div>
                                                        <p className="text-3xl font-bold text-[#1B4748] dark:text-gray-100">
                                                            {chats}
                                                        </p>
                                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                                            تعداد کل گفتگوها
                                                        </p>
                                                    </div>
                                                    <div onClick={() => navigate(`/bot/chatbox/${username}`)} className="text-sm cursor-pointer text-[#1B4748] dark:text-[#aadec5] flex items-center gap-1">
                                                        <span>مشاهده همه</span>
                                                        <ChevronRight className="w-4 h-4" />
                                                    </div>
                                                </div>
                                                {/* نوار پیشرفت */}
                                                <div className="mt-4 w-full h-1.5 bg-gray-100 dark:bg-zinc-700 rounded-full overflow-hidden">
                                                    <div
                                                        className="h-full bg-gradient-to-r from-[#1B4748] to-[#aadec5] rounded-full transition-all duration-1000"
                                                        style={{ width: `${Math.min(chats * 10, 100)}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>

                                    </div>


                                    {/* کارت آمار گفتگوها - ردیف دوم */}
                                    <div className="grid grid-cols-1 gap-6 mt-4">
                                        <div className="bg-gray-100/50 dark:bg-zinc-800/90 rounded-2xl shadow-lg border border-gray-100/50 dark:border-zinc-700/50 p-6">
                                            <div className="flex items-center gap-3 mb-4">
                                                <div className="p-2 rounded-xl bg-[#ff9900]/30">
                                                    <MessageSquare className="w-5 h-5 text-[#bb7000]" />
                                                </div>
                                                <h5 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                                                    آمار گفتگوها
                                                </h5>
                                            </div>
                                            <div className="flex items-end justify-between">
                                                <div>
                                                    <p className="text-3xl font-bold text-[#1B4748] dark:text-gray-100">
                                                        {chats}
                                                    </p>
                                                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                                        تعداد کل گفتگوها
                                                    </p>
                                                </div>
                                                <div onClick={() => navigate(`/bot/chatbox/${username}`)} className="text-sm cursor-pointer text-[#1B4748] dark:text-[#aadec5] flex items-center gap-1">
                                                    <span>مشاهده همه</span>
                                                    <ChevronRight className="w-4 h-4" />
                                                </div>
                                            </div>
                                            {/* نوار پیشرفت */}
                                            <div className="mt-4 w-full h-1.5 bg-gray-100 dark:bg-zinc-700 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-gradient-to-r from-[#1B4748] to-[#aadec5] rounded-full transition-all duration-1000"
                                                    style={{ width: `${Math.min(chats * 10, 100)}%` }}
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        <Footer />
                    </div>
                </div>
            </div>
        </>
    );
};

export default ClientDashboard;